/**
 * MapDisplay - ヘクスマップ表示コンポーネント
 *
 * FRONTEND.md Step.3 準拠:
 * - Lovable スタイル適用済み
 *
 * @see FRONTEND.md
 * @see docs/VISUAL_BLUEPRINT.md Section 2.3
 * @see src/types/ui-interfaces.ts
 */

import { useMemo, useState } from 'react';
import type { GoodsColor } from '../../types';
import type { MapDisplayProps } from '../../types/ui-interfaces';
import { useTranslations } from '../../i18n';
import { debug } from '../../utils/debug';
import { SeattleMapOverlay, SeattleDeliveredCubesOverlay } from './SeattleMapOverlay';

// 分割したモジュールからインポート
import {
  axialToPixel,
  getGoodsColorFill,
  getTrackColorByOwner,
  getTrackColorsByOwners,
  getEdgeMidpoint,
  getTileConnections,
} from './utils';
import { HexCell } from './HexCell';
import { ErrorBalloon, CostBalloon, IncomeBalloon } from './Balloons';

// 後方互換性のため再エクスポート
export { getTrackColorByOwner, getTrackColorsByOwners, axialToPixel, getEdgeMidpoint, getTileConnections };
export { renderTileTracks } from './trackRenderer';
export type { MapDisplayProps } from '../../types/ui-interfaces';

/**
 * ヘクスマップ表示コンポーネント
 *
 * 内部はPointy-top Hexで計算し、SVG全体を回転してFlat-top表示
 * - 縦表示: -90度回転
 * - 横表示: 0度回転
 *
 * VISUAL_BLUEPRINT 2.3 要件:
 * - マウスドラッグで全体を移動表示可能
 * - 右上はじに、回転（90度ずつ）、拡大縮小ボタン
 */
export function MapDisplay({
  hexes,
  onHexClick,
  onHexRightClick,
  temporaryTilePlacement,
  highlightedGoods,
  highlightedPath: _highlightedPath,
  highlightedCityIds,
  highlightIncome,
  highlightedTrackHexCoords,
  orientation = 'portrait',
  onToggleOrientation,
  zoomLevel = 1.0,
  onZoomIn,
  onZoomOut,
  panPosition = { x: 0, y: 0 },
  onPanChange: _onPanChange,
  ferryLinks = [],
  adjacentLinks = [],
  onLinkClick,
  temporaryLinkPlacement,
  highlightedSpecialLinkIds,
  seattleOverlay,
  placementError,
}: MapDisplayProps) {
  const hexSize = 30;
  const t = useTranslations();

  // 商品キューブ表示/非表示トグル
  const [showGoods, setShowGoods] = useState(true);

  // 回転角度
  const rotationAngle = orientation === 'portrait' ? -90 : 0;

  /**
   * エラーコードからローカライズメッセージを取得
   */
  const getLocalizedErrorMessage = (errorCode: string | undefined, fallbackMessage: string): string => {
    if (!errorCode) return fallbackMessage;

    // エラーコードとローカライズキーのマッピング
    const errorCodeToKey: Record<string, keyof typeof t.buildTrack.validation> = {
      'PLAYER_BANKRUPT': 'playerBankrupt',
      'INVALID_TILE_TYPE': 'invalidTileType',
      'INVALID_PLACEMENT_COUNT': 'maxTilesReached',
      'NEW_CITY_LIMIT_REACHED': 'newCityLimitReached',
      'NEW_CITY_NOT_AVAILABLE': 'newCityNotAvailable',
      'OUT_OF_BOUNDS': 'hexNotFound',
      'CANNOT_BUILD_ON_CITY': 'cannotBuildOnCity',
      'CANNOT_BUILD_ON_URBANIZED_TOWN': 'cannotBuildOnUrbanizedTown',
      'TOWN_TILE_REQUIRES_TOWN_HEX': 'townTileRequiresTownHex',
      'NEW_CITY_REQUIRES_TOWN_HEX': 'newCityRequiresTownHex',
      'URBANIZATION_REQUIRED': 'urbanizationRequired',
      'URBANIZATION_ALREADY_USED': 'urbanizationAlreadyUsed',
      'TOWN_DISK_ALREADY_PLACED': 'townDiskAlreadyPlaced',
      'NO_TOWN_DISKS_REMAINING': 'noTownDisksRemaining',
      'HEX_OCCUPIED': 'hexOccupied',
      'INVALID_CONNECTION': 'mustConnectToExisting',
      'FLOATING_TRACK_PATH': 'floatingTrackPath',
      'CANNOT_CONNECT_TO_MAP_EDGE': 'cannotConnectMapEdge',
      'CANNOT_CONNECT_TO_SEA': 'cannotConnectSea',
      'INSUFFICIENT_FUNDS': 'insufficientFunds',
      'WOULD_FORM_SELF_REFERENCING_LINK': 'selfReferencingLink',
      'INVALID_REPLACEMENT_PATTERN': 'invalidReplacementPattern',
      'INVALID_REPLACEMENT_CONNECTION': 'invalidReplacementConnection',
      'NEW_PATH_NOT_CONNECTED': 'newPathNotConnected',
      'NOT_AT_UNFINISHED_SECTION_END': 'notAtUnfinishedSectionEnd',
      'CANNOT_REPLACE_OTHER_PLAYER_TRACK': 'cannotReplaceOtherPlayerTrack',
      'REPLACEMENT_BREAKS_CONNECTION': 'replacementBreaksConnection',
      'CANNOT_REPLACE_COMPLETED_LINK': 'cannotReplaceCompletedLink',
      'CANNOT_MODIFY_OTHER_PLAYER_PATH': 'cannotModifyOtherPlayerPath',
      'PATH_NOT_AT_UNFINISHED_SECTION_END': 'pathNotAtUnfinishedSectionEnd',
      'TOWN_TILE_LIMIT_REACHED': 'townTileLimitReached',
      'NO_TRACK_TO_REPLACE': 'noTrackToReplace',
    };

    const key = errorCodeToKey[errorCode];
    if (key && t.buildTrack.validation[key]) {
      return t.buildTrack.validation[key];
    }

    return fallbackMessage;
  };

  // マップの境界ボックスを計算
  const { viewBoxWidth, viewBoxHeight, centerOffsetX, centerOffsetY, mapCenterX, mapCenterY } =
    useMemo(() => {
      if (hexes.length === 0) {
        return {
          viewBoxWidth: 800,
          viewBoxHeight: 600,
          centerOffsetX: 400,
          centerOffsetY: 300,
          mapCenterX: 400,
          mapCenterY: 300,
        };
      }

      let minX = Infinity,
        minY = Infinity;
      let maxX = -Infinity,
        maxY = -Infinity;

      hexes.forEach((hex) => {
        const { x, y } = axialToPixel(hex.coordinate.q, hex.coordinate.r, hexSize);
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });

      const padding = hexSize * 2;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      const viewBoxWidth = maxX - minX;
      const viewBoxHeight = maxY - minY;

      return {
        viewBoxWidth,
        viewBoxHeight,
        centerOffsetX: -minX,
        centerOffsetY: -minY,
        mapCenterX: viewBoxWidth / 2,
        mapCenterY: viewBoxHeight / 2,
      };
    }, [hexes, hexSize]);

  // エラーバルーン用の座標を計算
  const errorBalloonData = temporaryTilePlacement?.validationError
    ? (() => {
        const hex = hexes.find(
          (h) =>
            h.coordinate.q === temporaryTilePlacement.hexCoordinate.q &&
            h.coordinate.r === temporaryTilePlacement.hexCoordinate.r
        );
        if (!hex) return null;
        const { x, y } = axialToPixel(hex.coordinate.q, hex.coordinate.r, hexSize);
        // ローカライズメッセージを取得
        const localizedMessage = getLocalizedErrorMessage(
          temporaryTilePlacement.validationErrorCode,
          temporaryTilePlacement.validationError
        );
        return {
          x: x + centerOffsetX,
          y: y + centerOffsetY,
          message: localizedMessage,
        };
      })()
    : null;

  // 配置エラーバルーン用の座標を計算（Phase 9 商品配置で満杯 City/Town をクリックした場合）
  const placementErrorBalloonData = placementError
    ? (() => {
        const { x, y } = axialToPixel(placementError.coordinate.q, placementError.coordinate.r, hexSize);
        return {
          x: x + centerOffsetX,
          y: y + centerOffsetY,
          message: placementError.message,
        };
      })()
    : null;

  // コストバルーン用の座標を計算（バリデーション成功時のみ）
  const costBalloonData = temporaryTilePlacement && !temporaryTilePlacement.validationError && temporaryTilePlacement.cost !== undefined
    ? (() => {
        const hex = hexes.find(
          (h) =>
            h.coordinate.q === temporaryTilePlacement.hexCoordinate.q &&
            h.coordinate.r === temporaryTilePlacement.hexCoordinate.r
        );
        if (!hex) return null;
        const { x, y } = axialToPixel(hex.coordinate.q, hex.coordinate.r, hexSize);
        return {
          x: x + centerOffsetX,
          y: y + centerOffsetY,
          cost: temporaryTilePlacement.cost,
        };
      })()
    : null;

  // 特殊リンク仮配置用のバルーンデータを計算
  const linkBalloonData = temporaryLinkPlacement
    ? (() => {
        // リンクを探す
        const allLinks = [...ferryLinks, ...adjacentLinks];
        const link = allLinks.find(l => l.id === temporaryLinkPlacement.linkId);
        if (!link) return null;

        const fromPixel = axialToPixel(link.from.q, link.from.r, hexSize);
        const toPixel = axialToPixel(link.to.q, link.to.r, hexSize);
        const fromX = fromPixel.x + centerOffsetX;
        const fromY = fromPixel.y + centerOffsetY;
        const toX = toPixel.x + centerOffsetX;
        const toY = toPixel.y + centerOffsetY;

        // フェリーリンクは曲線の中点を使用
        if (temporaryLinkPlacement.linkType === 'ferry') {
          const sameHexLinks = ferryLinks.filter(
            l =>
              (l.from.q === link.from.q && l.from.r === link.from.r &&
               l.to.q === link.to.q && l.to.r === link.to.r) ||
              (l.from.q === link.to.q && l.from.r === link.to.r &&
               l.to.q === link.from.q && l.to.r === link.from.r)
          );
          const linkIndexInGroup = sameHexLinks.findIndex(l => l.id === link.id);
          const totalLinks = sameHexLinks.length;

          let offsetMultiplier = 1.0;
          if (totalLinks === 2) {
            offsetMultiplier = linkIndexInGroup === 0 ? 1.0 : -1.0;
          } else if (totalLinks === 3) {
            offsetMultiplier = [1.0, 0, -1.0][linkIndexInGroup];
          } else if (totalLinks > 3) {
            const step = 2.0 / (totalLinks - 1);
            offsetMultiplier = 1.0 - linkIndexInGroup * step;
          }

          const midX = (fromX + toX) / 2;
          const midY = (fromY + toY) / 2;
          const dx = toX - fromX;
          const dy = toY - fromY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const perpX = -dy / distance;
          const perpY = dx / distance;
          const curveOffset = distance * 0.3 * offsetMultiplier;
          const ctrlX = midX + perpX * curveOffset;
          const ctrlY = midY + perpY * curveOffset;

          // 曲線上の中点
          const x = fromX * 0.25 + ctrlX * 0.5 + toX * 0.25;
          const y = fromY * 0.25 + ctrlY * 0.5 + toY * 0.25;

          return {
            x,
            y,
            cost: temporaryLinkPlacement.cost,
            validationError: temporaryLinkPlacement.validationError,
            validationErrorCode: temporaryLinkPlacement.validationErrorCode,
          };
        } else {
          // 隣接リンクは中点
          return {
            x: (fromX + toX) / 2,
            y: (fromY + toY) / 2,
            cost: temporaryLinkPlacement.cost,
            validationError: temporaryLinkPlacement.validationError,
            validationErrorCode: temporaryLinkPlacement.validationErrorCode,
          };
        }
      })()
    : null;

  return (
    <div className="relative w-full h-full bg-terrain-water/30 overflow-hidden">
      {/* Seattle Metro: 輸送済みキューブ数オーバーレイ（左上） */}
      {seattleOverlay && (
        <SeattleDeliveredCubesOverlay deliveredCubes={seattleOverlay.deliveredCubes} />
      )}

      {/* コントロールパネル（右上配置） */}
      <div className="absolute top-1 right-1 md:top-2 md:right-2 z-10 flex gap-0.5 md:gap-1">
        {/* 商品表示トグルボタン */}
        <button
          className={`btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs min-w-[28px] md:min-w-[32px] ${!showGoods ? 'opacity-50' : ''}`}
          onClick={() => setShowGoods(!showGoods)}
          data-action="toggle-goods"
          title={showGoods ? 'Hide goods' : 'Show goods'}
        >
          <span className="inline-block w-2.5 h-2.5 md:w-3 md:h-3 rounded-sm bg-goods-red border border-foreground/50"></span>
        </button>

        {/* 回転ボタン */}
        {onToggleOrientation && (
          <button
            className="btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs min-w-[28px] md:min-w-[32px]"
            onClick={onToggleOrientation}
            data-action="rotate"
          >
            {orientation === 'portrait' ? '⟳' : '⟲'}
          </button>
        )}

        {/* ズームボタン */}
        {onZoomIn && (
          <button
            className="btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs font-bold min-w-[28px] md:min-w-[32px]"
            onClick={onZoomIn}
            data-action="zoom-in"
          >
            +
          </button>
        )}
        {onZoomOut && (
          <button
            className="btn-secondary px-1.5 py-0.5 md:px-2 md:py-1 text-xs font-bold min-w-[28px] md:min-w-[32px]"
            onClick={onZoomOut}
            data-action="zoom-out"
          >
            −
          </button>
        )}

        {/* ズームレベル表示 - デスクトップのみ */}
        <span className="hidden md:inline-block bg-card/80 px-2 py-1 rounded text-xs font-mono text-muted-foreground">
          {Math.round(zoomLevel * 100)}%
        </span>
      </div>

      {/* SVGマップ（ズーム・パン対応） */}
      <svg
        className="w-full h-full"
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: 'visible', userSelect: 'none' }}
      >
        {/* ズーム・パン用ラッパー */}
        <g
          transform={`translate(${mapCenterX + panPosition.x}, ${mapCenterY + panPosition.y}) scale(${zoomLevel}) translate(${-mapCenterX}, ${-mapCenterY})`}
        >
          <g transform={`rotate(${rotationAngle}, ${mapCenterX}, ${mapCenterY})`}>
          {/* フェリーリンク破線（ヘクスタイルの背面に描画） - 未所有/仮配置のみ */}
          {ferryLinks.map((link, linkIndex) => {
            const isOwned = link.ownerColor !== null;
            const isHighlighted = highlightedSpecialLinkIds?.some(
              hl => hl.linkType === 'ferry' && hl.linkId === link.id
            );
            // 所有済みまたは経路強調は別レイヤーで描画するためスキップ
            if (isOwned || isHighlighted) return null;

            // 同じヘクスペア間の複数リンクを取得
            const sameHexLinks = ferryLinks.filter(
              l =>
                (l.from.q === link.from.q && l.from.r === link.from.r &&
                 l.to.q === link.to.q && l.to.r === link.to.r) ||
                (l.from.q === link.to.q && l.from.r === link.to.r &&
                 l.to.q === link.from.q && l.to.r === link.from.r)
            );
            const linkIndexInGroup = sameHexLinks.findIndex(l => l.id === link.id);
            const totalLinks = sameHexLinks.length;

            // オフセット計算（逆方向の弧を描く: 正負で交互）
            let offsetMultiplier = 1.0;
            if (totalLinks === 2) {
              offsetMultiplier = linkIndexInGroup === 0 ? 1.0 : -1.0;
            } else if (totalLinks === 3) {
              offsetMultiplier = [1.0, 0, -1.0][linkIndexInGroup];
            } else if (totalLinks > 3) {
              const step = 2.0 / (totalLinks - 1);
              offsetMultiplier = 1.0 - linkIndexInGroup * step;
            }

            const fromPixel = axialToPixel(link.from.q, link.from.r, hexSize);
            const toPixel = axialToPixel(link.to.q, link.to.r, hexSize);
            const fromX = fromPixel.x + centerOffsetX;
            const fromY = fromPixel.y + centerOffsetY;
            const toX = toPixel.x + centerOffsetX;
            const toY = toPixel.y + centerOffsetY;

            const midX = (fromX + toX) / 2;
            const midY = (fromY + toY) / 2;
            const dx = toX - fromX;
            const dy = toY - fromY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const perpX = -dy / distance;
            const perpY = dx / distance;
            const curveOffset = distance * 0.3 * offsetMultiplier;
            const ctrlX = midX + perpX * curveOffset;
            const ctrlY = midY + perpY * curveOffset;

            // 仮配置中かどうか
            const isTentative = temporaryLinkPlacement?.linkType === 'ferry' && temporaryLinkPlacement?.linkId === link.id;
            const hasTentativeError = isTentative && !!temporaryLinkPlacement?.validationError;

            let strokeColor = "hsl(var(--muted-foreground))";
            if (isTentative) {
              strokeColor = hasTentativeError ? "hsl(var(--destructive))" : "hsl(var(--primary))";
            }
            const strokeDash: string | undefined = "6,4";
            let strokeW = 2;
            if (isTentative) strokeW = 6; // 仮配置中は太く
            // クリック可能
            const isClickable = !!onLinkClick;

            return (
              <path
                key={`ferry-line-${link.id}-${linkIndex}`}
                d={`M ${fromX} ${fromY} Q ${ctrlX} ${ctrlY} ${toX} ${toY}`}
                fill="none"
                stroke={strokeColor}
                strokeWidth={isClickable ? strokeW + 4 : strokeW}
                strokeDasharray={isTentative ? undefined : strokeDash}
                strokeLinecap="round"
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
                onClick={isClickable ? () => onLinkClick('ferry', link.id) : undefined}
                pointerEvents={isClickable ? 'stroke' : 'none'}
              />
            );
          })}

          {/* 隣接リンク破線（ヘクスタイルの背面に描画） - 未所有/仮配置のみ */}
          {adjacentLinks.map((link, linkIndex) => {
            const isOwned = link.ownerColor !== null;
            const isHighlighted = highlightedSpecialLinkIds?.some(
              hl => hl.linkType === 'adjacent' && hl.linkId === link.id
            );
            // 所有済みまたは経路強調は別レイヤーで描画するためスキップ
            if (isOwned || isHighlighted) return null;

            const fromPixel = axialToPixel(link.from.q, link.from.r, hexSize);
            const toPixel = axialToPixel(link.to.q, link.to.r, hexSize);
            const fromX = fromPixel.x + centerOffsetX;
            const fromY = fromPixel.y + centerOffsetY;
            const toX = toPixel.x + centerOffsetX;
            const toY = toPixel.y + centerOffsetY;

            // 仮配置中かどうか
            const isTentative = temporaryLinkPlacement?.linkType === 'adjacent' && temporaryLinkPlacement?.linkId === link.id;
            const hasTentativeError = isTentative && !!temporaryLinkPlacement?.validationError;

            let strokeColor = "hsl(var(--muted-foreground))";
            if (isTentative) {
              strokeColor = hasTentativeError ? "hsl(var(--destructive))" : "hsl(var(--primary))";
            }
            const strokeDash: string | undefined = "4,2";
            let strokeW = 2;
            if (isTentative) strokeW = 6; // 仮配置中は太く
            // クリック可能かどうか（未所有かつコールバックあり）
            const isClickable = !!onLinkClick;

            return (
              <line
                key={`adjacent-line-${link.id}-${linkIndex}`}
                x1={fromX}
                y1={fromY}
                x2={toX}
                y2={toY}
                stroke={strokeColor}
                strokeWidth={isClickable ? strokeW + 4 : strokeW}
                strokeDasharray={isTentative ? undefined : strokeDash}
                strokeLinecap="round"
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
                onClick={isClickable ? () => onLinkClick('adjacent', link.id) : undefined}
                pointerEvents={isClickable ? 'stroke' : 'none'}
              />
            );
          })}

          {/* 敷設済み・経路強調のフェリーリンク（ヘクスより背面に描画） */}
          {ferryLinks.map((link, linkIndex) => {
            const isOwned = link.ownerColor !== null;
            const isHighlighted = highlightedSpecialLinkIds?.some(
              hl => hl.linkType === 'ferry' && hl.linkId === link.id
            );
            // 所有済みまたは経路強調の場合のみ描画
            if (!isOwned && !isHighlighted) return null;

            const sameHexLinks = ferryLinks.filter(
              l =>
                (l.from.q === link.from.q && l.from.r === link.from.r &&
                 l.to.q === link.to.q && l.to.r === link.to.r) ||
                (l.from.q === link.to.q && l.from.r === link.to.r &&
                 l.to.q === link.from.q && l.to.r === link.from.r)
            );
            const linkIndexInGroup = sameHexLinks.findIndex(l => l.id === link.id);
            const totalLinks = sameHexLinks.length;

            let offsetMultiplier = 1.0;
            if (totalLinks === 2) {
              offsetMultiplier = linkIndexInGroup === 0 ? 1.0 : -1.0;
            } else if (totalLinks === 3) {
              offsetMultiplier = [1.0, 0, -1.0][linkIndexInGroup];
            } else if (totalLinks > 3) {
              const step = 2.0 / (totalLinks - 1);
              offsetMultiplier = 1.0 - linkIndexInGroup * step;
            }

            const fromPixel = axialToPixel(link.from.q, link.from.r, hexSize);
            const toPixel = axialToPixel(link.to.q, link.to.r, hexSize);
            const fromX = fromPixel.x + centerOffsetX;
            const fromY = fromPixel.y + centerOffsetY;
            const toX = toPixel.x + centerOffsetX;
            const toY = toPixel.y + centerOffsetY;

            const midX = (fromX + toX) / 2;
            const midY = (fromY + toY) / 2;
            const dx = toX - fromX;
            const dy = toY - fromY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const perpX = -dy / distance;
            const perpY = dx / distance;
            const curveOffset = distance * 0.3 * offsetMultiplier;
            const ctrlX = midX + perpX * curveOffset;
            const ctrlY = midY + perpY * curveOffset;

            // 強調時は緑（通常線路と同じ）、それ以外はプレイヤー色
            const strokeColor = isHighlighted ? "hsl(142, 76%, 36%)" : link.ownerColor!;
            const strokeW = isHighlighted ? 6 : 4;

            return (
              <path
                key={`ferry-owned-${link.id}-${linkIndex}`}
                d={`M ${fromX} ${fromY} Q ${ctrlX} ${ctrlY} ${toX} ${toY}`}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeW}
                strokeLinecap="round"
                style={isHighlighted ? { animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
              />
            );
          })}

          {hexes.map((hex) => {
            const { x, y } = axialToPixel(hex.coordinate.q, hex.coordinate.r, hexSize);
            const centerX = x + centerOffsetX;
            const centerY = y + centerOffsetY;

            const isTemporary = !!(
              temporaryTilePlacement &&
              temporaryTilePlacement.hexCoordinate.q === hex.coordinate.q &&
              temporaryTilePlacement.hexCoordinate.r === hex.coordinate.r
            );

            // 都市/町/新都市がハイライト対象かチェック
            // ※ 新都市がある場合は新都市IDを優先（町IDより先に評価）
            const cityId = hex.city?.id || hex.town?.newCity?.id || hex.town?.id;
            const isHighlightedCity = !!(cityId && highlightedCityIds?.includes(cityId));

            // 線路がハイライト対象かチェック + Edgeペア情報を収集
            const matchingHighlights = hex.tile && highlightedTrackHexCoords?.filter(
              coord => coord.q === hex.coordinate.q && coord.r === hex.coordinate.r
            );
            const isHighlightedTrack = !!(matchingHighlights && matchingHighlights.length > 0);
            // edgePairが指定されている場合はそれを収集（segmentIndex付き）、なければ全体強調
            const highlightedEdgePairs = matchingHighlights
              ?.filter(h => h.edgePair)
              .map(h => ({ pair: h.edgePair as [number, number], segmentIndex: h.segmentIndex ?? 0 }));
            const totalHighlightCount = highlightedTrackHexCoords?.length;

            // DEBUG: 強調対象のヘクスの情報を出力
            if (highlightedTrackHexCoords?.some(c => c.q === hex.coordinate.q && c.r === hex.coordinate.r)) {
              debug.log(`[MapDisplay] Hex (${hex.coordinate.q},${hex.coordinate.r}):`, {
                hasTile: !!hex.tile,
                tileType: hex.tile?.type,
                tileRotation: hex.tile?.rotation,
                isCity: !!hex.city,
                isTown: !!hex.town,
                cityId,
                highlightedCityIds,
                isHighlightedCity,
                'hex.city?.id': hex.city?.id,
                'hex.town?.id': hex.town?.id,
                'hex.town?.newCity?.id': hex.town?.newCity?.id,
                matchingHighlights,
                isHighlightedTrack,
                highlightedEdgePairs,
                highlightedEdgePairsAfterRotation: highlightedEdgePairs?.map(hp => ({
                  pair: [
                    hp.pair[0] === -1 ? -1 : ((hp.pair[0] - (hex.tile?.rotation || 0)) % 6 + 6) % 6,
                    hp.pair[1] === -1 ? -1 : ((hp.pair[1] - (hex.tile?.rotation || 0)) % 6 + 6) % 6
                  ],
                  segmentIndex: hp.segmentIndex
                }))
              });
            }

            return (
              <HexCell
                key={`${hex.coordinate.q},${hex.coordinate.r}`}
                hex={hex}
                centerX={centerX}
                centerY={centerY}
                hexSize={hexSize}
                rotationAngle={rotationAngle}
                onClick={onHexClick ? () => onHexClick(hex) : undefined}
                onRightClick={onHexRightClick ? () => onHexRightClick(hex) : undefined}
                isTemporary={isTemporary}
                temporaryTileType={temporaryTilePlacement?.tileType}
                temporaryRotation={temporaryTilePlacement?.rotation}
                isClickable={!!onHexClick}
                hasValidationError={isTemporary && !!temporaryTilePlacement?.validationError}
                validationErrorMessage={isTemporary ? temporaryTilePlacement?.validationError : null}
                isHighlightedCity={isHighlightedCity}
                skipGoodsRendering={true}
                isHighlightedTrack={isHighlightedTrack}
                highlightedEdgePairs={highlightedEdgePairs && highlightedEdgePairs.length > 0 ? highlightedEdgePairs : undefined}
                totalHighlightCount={totalHighlightCount}
              />
            );
          })}

          {/* 壁の描画（赤い太線）- ヘクスより前面、都市名より背面 */}
          {hexes.map((hex) => {
            if (!hex.blockedEdges || hex.blockedEdges.length === 0) return null;
            const { x, y } = axialToPixel(hex.coordinate.q, hex.coordinate.r, hexSize);
            const centerX = x + centerOffsetX;
            const centerY = y + centerOffsetY;

            return (
              <g key={`walls-${hex.coordinate.q},${hex.coordinate.r}`}>
                {hex.blockedEdges.map((edge) => {
                  // 辺の両端の頂点を計算
                  const vertex1Angle = (edge * 60) * Math.PI / 180;
                  const vertex2Angle = ((edge + 1) * 60) * Math.PI / 180;
                  const v1x = centerX + hexSize * Math.sin(vertex1Angle);
                  const v1y = centerY - hexSize * Math.cos(vertex1Angle);
                  const v2x = centerX + hexSize * Math.sin(vertex2Angle);
                  const v2y = centerY - hexSize * Math.cos(vertex2Angle);

                  return (
                    <line
                      key={`wall-${hex.coordinate.q}-${hex.coordinate.r}-${edge}`}
                      x1={v1x}
                      y1={v1y}
                      x2={v2x}
                      y2={v2y}
                      stroke="#DC2626"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                  );
                })}
              </g>
            );
          })}

          {/* 都市名をタイルより前面に描画（全ヘクスの後、商品キューブの前） */}
          {hexes.map((hex) => {
            if (!hex.city) return null;
            const { x, y } = axialToPixel(hex.coordinate.q, hex.coordinate.r, hexSize);
            const centerX = x + centerOffsetX;
            const centerY = y + centerOffsetY;

            return (
              <g
                key={`cityname-${hex.coordinate.q},${hex.coordinate.r}`}
                className="city-name-layer"
                transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}
                style={{ pointerEvents: 'none' }}
              >
                <defs>
                  <path
                    id={`cityNamePath-front-${hex.city.id}`}
                    d={`M ${centerX - hexSize * 0.7} ${centerY - hexSize * 0.3} A ${hexSize * 0.7} ${hexSize * 0.7} 0 0 1 ${centerX + hexSize * 0.7} ${centerY - hexSize * 0.3}`}
                    fill="none"
                  />
                </defs>
                <text className="text-[6px] fill-foreground/70 font-medium" style={{ userSelect: 'none' }}>
                  <textPath
                    href={`#cityNamePath-front-${hex.city.id}`}
                    startOffset="50%"
                    textAnchor="middle"
                  >
                    {hex.city.name.substring(0, 10)}
                  </textPath>
                </text>
              </g>
            );
          })}

          {/* 敷設済み・経路強調の隣接リンク（都市名より前面、商品キューブより背面） */}
          {adjacentLinks.map((link, linkIndex) => {
            const isOwned = link.ownerColor !== null;
            const isHighlighted = highlightedSpecialLinkIds?.some(
              hl => hl.linkType === 'adjacent' && hl.linkId === link.id
            );
            // 所有済みまたは経路強調の場合のみ描画
            if (!isOwned && !isHighlighted) return null;

            const fromPixel = axialToPixel(link.from.q, link.from.r, hexSize);
            const toPixel = axialToPixel(link.to.q, link.to.r, hexSize);
            const fromX = fromPixel.x + centerOffsetX;
            const fromY = fromPixel.y + centerOffsetY;
            const toX = toPixel.x + centerOffsetX;
            const toY = toPixel.y + centerOffsetY;

            // 都市の丸の縁から縁までの短い線を描画
            const dx = toX - fromX;
            const dy = toY - fromY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const unitX = dx / dist;
            const unitY = dy / dist;
            const cityRadius = hexSize * 0.7;
            const lineFromX = fromX + unitX * cityRadius;
            const lineFromY = fromY + unitY * cityRadius;
            const lineToX = toX - unitX * cityRadius;
            const lineToY = toY - unitY * cityRadius;

            // 強調時は緑（通常線路と同じ）、それ以外はプレイヤー色
            const strokeColor = isHighlighted ? "hsl(142, 76%, 36%)" : link.ownerColor!;
            const strokeW = isHighlighted ? 6 : 4;

            return (
              <line
                key={`adjacent-owned-${link.id}-${linkIndex}`}
                x1={lineFromX}
                y1={lineFromY}
                x2={lineToX}
                y2={lineToY}
                stroke={strokeColor}
                strokeWidth={strokeW}
                strokeLinecap="round"
                style={isHighlighted ? { animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
              />
            );
          })}

          {/* Seattle Metro 専用オーバーレイ（隣接リンクより前面、商品キューブより背面） */}
          {seattleOverlay && (
            <SeattleMapOverlay
              data={seattleOverlay}
              hexSize={hexSize}
              centerOffsetX={centerOffsetX}
              centerOffsetY={centerOffsetY}
              rotationAngle={rotationAngle}
            />
          )}

          {/* 商品キューブ（隣接リンクより前面） - showGoods が true の場合のみ */}
          {showGoods && hexes.map((hex) => {
            if (!hex.goods || hex.goods.length === 0) return null;
            const { x, y } = axialToPixel(hex.coordinate.q, hex.coordinate.r, hexSize);
            const centerX = x + centerOffsetX;
            const centerY = y + centerOffsetY;
            const cubeSize = 12;
            const cubeSpacing = cubeSize + 2;

            // 都市IDを取得（新都市がある場合は新都市ID、なければ町ID）
            const cityId = hex.city?.id || hex.town?.newCity?.id || hex.town?.id;

            // 上部に配置するキューブ（0-2番目、最大3個）
            const topGoods = hex.goods.slice(0, 3);
            // 下部に配置するキューブ（3-5番目）
            const bottomGoods = hex.goods.slice(3, 6);

            return (
              <g
                key={`goods-${hex.coordinate.q},${hex.coordinate.r}`}
                className="goods-layer"
                transform={`rotate(${-rotationAngle}, ${centerX}, ${centerY})`}
              >
                {/* 上部の商品キューブ（1-3個目） */}
                {topGoods.map((goodsColor: GoodsColor, idx: number) => {
                  const isHighlighted = highlightedGoods?.cityId === cityId && highlightedGoods?.color === goodsColor;
                  const x = centerX + (idx - topGoods.length / 2 + 0.5) * cubeSpacing - cubeSize / 2;
                  const y = centerY - hexSize * 0.85;
                  return (
                    <g key={`top-${idx}`}>
                      {/* 塗りつぶし（アニメーションなし） */}
                      <rect
                        x={x}
                        y={y}
                        width={cubeSize}
                        height={cubeSize}
                        rx="2"
                        fill={getGoodsColorFill(goodsColor)}
                        stroke={goodsColor === 'black' ? 'white' : 'hsl(var(--foreground))'}
                        strokeWidth="1"
                      />
                      {/* ハイライト時の枠線（アニメーション付き） */}
                      {isHighlighted && (
                        <rect
                          x={x}
                          y={y}
                          width={cubeSize}
                          height={cubeSize}
                          rx="2"
                          fill="none"
                          stroke="hsl(142, 76%, 36%)"
                          strokeWidth="4"
                          style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
                        />
                      )}
                    </g>
                  );
                })}

                {/* 下部の商品キューブ（4-6個目） */}
                {bottomGoods.map((goodsColor: GoodsColor, idx: number) => {
                  const isHighlighted = highlightedGoods?.cityId === cityId && highlightedGoods?.color === goodsColor;
                  const x = centerX + (idx - bottomGoods.length / 2 + 0.5) * cubeSpacing - cubeSize / 2;
                  const y = centerY + hexSize * 0.5;
                  return (
                    <g key={`bottom-${idx}`}>
                      {/* 塗りつぶし（アニメーションなし） */}
                      <rect
                        x={x}
                        y={y}
                        width={cubeSize}
                        height={cubeSize}
                        rx="2"
                        fill={getGoodsColorFill(goodsColor)}
                        stroke={goodsColor === 'black' ? 'white' : 'hsl(var(--foreground))'}
                        strokeWidth="1"
                      />
                      {/* ハイライト時の枠線（アニメーション付き） */}
                      {isHighlighted && (
                        <rect
                          x={x}
                          y={y}
                          width={cubeSize}
                          height={cubeSize}
                          rx="2"
                          fill="none"
                          stroke="hsl(142, 76%, 36%)"
                          strokeWidth="4"
                          style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
                        />
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* フェリーリンクコスト表記（商品キューブより前面、バルーンより背面） */}
          {ferryLinks.map((link, linkIndex) => {
            const isOwned = link.ownerColor !== null;
            // 所有済みの場合は表示しない
            if (isOwned) return null;

            const sameHexLinks = ferryLinks.filter(
              l =>
                (l.from.q === link.from.q && l.from.r === link.from.r &&
                 l.to.q === link.to.q && l.to.r === link.to.r) ||
                (l.from.q === link.to.q && l.from.r === link.to.r &&
                 l.to.q === link.from.q && l.to.r === link.from.r)
            );
            const linkIndexInGroup = sameHexLinks.findIndex(l => l.id === link.id);
            const totalLinks = sameHexLinks.length;

            let offsetMultiplier = 1.0;
            if (totalLinks === 2) {
              offsetMultiplier = linkIndexInGroup === 0 ? 1.0 : -1.0;
            } else if (totalLinks === 3) {
              offsetMultiplier = [1.0, 0, -1.0][linkIndexInGroup];
            } else if (totalLinks > 3) {
              const step = 2.0 / (totalLinks - 1);
              offsetMultiplier = 1.0 - linkIndexInGroup * step;
            }

            const fromPixel = axialToPixel(link.from.q, link.from.r, hexSize);
            const toPixel = axialToPixel(link.to.q, link.to.r, hexSize);
            const fromX = fromPixel.x + centerOffsetX;
            const fromY = fromPixel.y + centerOffsetY;
            const toX = toPixel.x + centerOffsetX;
            const toY = toPixel.y + centerOffsetY;

            const midX = (fromX + toX) / 2;
            const midY = (fromY + toY) / 2;
            const dx = toX - fromX;
            const dy = toY - fromY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const perpX = -dy / distance;
            const perpY = dx / distance;
            const curveOffset = distance * 0.3 * offsetMultiplier;
            const ctrlX = midX + perpX * curveOffset;
            const ctrlY = midY + perpY * curveOffset;

            // コスト表示位置（曲線上の中点）
            const costX = fromX * 0.25 + ctrlX * 0.5 + toX * 0.25;
            const costY = fromY * 0.25 + ctrlY * 0.5 + toY * 0.25;

            const isClickable = !!onLinkClick;
            const isTentative = temporaryLinkPlacement?.linkType === 'ferry' && temporaryLinkPlacement?.linkId === link.id;
            const hasTentativeError = isTentative && !!temporaryLinkPlacement?.validationError;

            return (
              <g
                key={`ferry-cost-${link.id}-${linkIndex}`}
                transform={`rotate(${-rotationAngle}, ${costX}, ${costY})`}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
                onClick={isClickable ? () => onLinkClick('ferry', link.id) : undefined}
              >
                <rect
                  x={costX - 12}
                  y={costY - 8}
                  width="24"
                  height="16"
                  rx="4"
                  fill="hsl(var(--background))"
                  stroke={isTentative ? (hasTentativeError ? "hsl(var(--destructive))" : "hsl(var(--primary))") : "hsl(var(--muted-foreground))"}
                  strokeWidth={isTentative ? 3 : 1}
                />
                <text
                  x={costX}
                  y={costY + 4}
                  textAnchor="middle"
                  className="text-[10px] fill-muted-foreground font-bold"
                  style={{ pointerEvents: 'none' }}
                >
                  ${link.cost}
                </text>
              </g>
            );
          })}

          {/* 隣接リンクコスト表記（商品キューブより前面、バルーンより背面） */}
          {adjacentLinks.map((link, linkIndex) => {
            const isOwned = link.ownerColor !== null;
            // 所有済みの場合は表示しない
            if (isOwned) return null;

            const fromPixel = axialToPixel(link.from.q, link.from.r, hexSize);
            const toPixel = axialToPixel(link.to.q, link.to.r, hexSize);
            const fromX = fromPixel.x + centerOffsetX;
            const fromY = fromPixel.y + centerOffsetY;
            const toX = toPixel.x + centerOffsetX;
            const toY = toPixel.y + centerOffsetY;

            const midX = (fromX + toX) / 2;
            const midY = (fromY + toY) / 2;

            const isClickable = !!onLinkClick;
            const isTentative = temporaryLinkPlacement?.linkType === 'adjacent' && temporaryLinkPlacement?.linkId === link.id;
            const hasTentativeError = isTentative && !!temporaryLinkPlacement?.validationError;

            return (
              <g
                key={`adjacent-cost-${link.id}-${linkIndex}`}
                transform={`rotate(${-rotationAngle}, ${midX}, ${midY})`}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
                onClick={isClickable ? () => onLinkClick('adjacent', link.id) : undefined}
              >
                <circle
                  cx={midX}
                  cy={midY}
                  r="12"
                  fill="hsl(var(--background))"
                  stroke={isTentative ? (hasTentativeError ? "hsl(var(--destructive))" : "hsl(var(--primary))") : "hsl(var(--muted-foreground))"}
                  strokeWidth={isTentative ? 3 : 2}
                />
                <text
                  x={midX}
                  y={midY + 4}
                  textAnchor="middle"
                  className="text-[9px] fill-muted-foreground font-bold"
                  style={{ pointerEvents: 'none' }}
                >
                  ${link.cost}
                </text>
              </g>
            );
          })}

          {/* エラーバルーン（最前面に表示） */}
          {errorBalloonData && (
            <g transform={`rotate(${-rotationAngle}, ${errorBalloonData.x}, ${errorBalloonData.y})`}>
              <ErrorBalloon
                x={errorBalloonData.x + hexSize * 0.8}
                y={errorBalloonData.y - hexSize * 0.8}
                message={errorBalloonData.message}
              />
            </g>
          )}

          {/* 配置エラーバルーン（Phase 9 商品配置で満杯 City/Town クリック時） */}
          {placementErrorBalloonData && (
            <g transform={`rotate(${-rotationAngle}, ${placementErrorBalloonData.x}, ${placementErrorBalloonData.y})`}>
              <ErrorBalloon
                x={placementErrorBalloonData.x + hexSize * 0.8}
                y={placementErrorBalloonData.y - hexSize * 0.8}
                message={placementErrorBalloonData.message}
              />
            </g>
          )}

          {/* コストバルーン（バリデーション成功時に表示） */}
          {costBalloonData && (
            <g transform={`rotate(${-rotationAngle}, ${costBalloonData.x}, ${costBalloonData.y})`}>
              <CostBalloon
                x={costBalloonData.x + hexSize * 0.8}
                y={costBalloonData.y - hexSize * 0.8}
                cost={costBalloonData.cost}
              />
            </g>
          )}

          {/* 特殊リンク仮配置バルーン */}
          {linkBalloonData && (
            <g transform={`rotate(${-rotationAngle}, ${linkBalloonData.x}, ${linkBalloonData.y})`}>
              {linkBalloonData.validationError ? (
                <ErrorBalloon
                  x={linkBalloonData.x}
                  y={linkBalloonData.y - hexSize * 0.8}
                  message={getLocalizedErrorMessage(linkBalloonData.validationErrorCode, linkBalloonData.validationError)}
                />
              ) : (
                <CostBalloon
                  x={linkBalloonData.x}
                  y={linkBalloonData.y - hexSize * 0.8}
                  cost={linkBalloonData.cost}
                />
              )}
            </g>
          )}

          {/* Incomeバルーン（商品選択時に表示、目的地都市の右上） */}
          {highlightIncome && highlightedCityIds && highlightedCityIds.length >= 1 && (() => {
            // 目的地のHexを取得（highlightedCityIds[0]が目的地）
            const destCityId = highlightedCityIds[0];
            const destHex = hexes.find((h) => {
              const cityId = h.city?.id || h.town?.newCity?.id;
              return cityId === destCityId;
            });
            if (!destHex) return null;
            const { x, y } = axialToPixel(destHex.coordinate.q, destHex.coordinate.r, hexSize);
            const balloonX = x + centerOffsetX;
            const balloonY = y + centerOffsetY;
            return (
              <g transform={`rotate(${-rotationAngle}, ${balloonX}, ${balloonY})`}>
                <IncomeBalloon
                  x={balloonX + hexSize * 0.9}
                  y={balloonY - hexSize * 0.6}
                  income={highlightIncome}
                />
              </g>
            );
          })()}

          </g>
        </g>
      </svg>

      {/* マップ情報 - デスクトップのみ */}
      <div className="hidden md:block absolute bottom-2 left-2 bg-card/80 px-2 py-1 rounded text-xs text-muted-foreground">
        Map: {hexes.length} hexes
      </div>
    </div>
  );
}

export default MapDisplay;
